import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UIProvider } from "@/context/UIContext";
import { PermissionsProvider } from "@/context/PermissionsContext";
import { ChatProvider } from "@/context/ChatContext";
import { GlobalModals } from "@/components/GlobalModals";
import { ChatDrawer } from "@/components/chat/ChatDrawer";
import { ChatWindows } from "@/components/chat/ChatWindows";
import { Loading } from "@/components/shared/UI";
import AppShell, { RequirePage } from "@/components/layout/AppShell";
import { ForcedSetup } from "@/components/ForcedSetup";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
const Organisations = lazy(() => import("@/pages/Organisations"));
const GoogleCallback = lazy(() => import("@/pages/GoogleCallback"));
const OrganisationDetail = lazy(() => import("@/pages/OrganisationDetail"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const ContactDetail = lazy(() => import("@/pages/ContactDetail"));
const Properties = lazy(() => import("@/pages/Properties"));
const PropertyDetail = lazy(() => import("@/pages/PropertyDetail"));
const MyActions = lazy(() => import("@/pages/MyActions"));
const ToDo = lazy(() => import("@/pages/ToDo"));
const Opportunities = lazy(() => import("@/pages/Opportunities"));
const Collaborations = lazy(() => import("@/pages/Collaborations"));
const OpportunityDetail = lazy(() => import("@/pages/OpportunityDetail"));
const Settings = lazy(() => import("@/pages/Settings"));
const KPIs = lazy(() => import("@/pages/KPIs"));
const Reports = lazy(() => import("@/pages/Reports"));
const Stats = lazy(() => import("@/pages/Stats"));
const Forecasts = lazy(() => import("@/pages/Forecasts"));
const PrintOrders = lazy(() => import("@/pages/PrintOrders"));
const RatesManagement = lazy(() => import("@/pages/RatesManagement"));
const AlcoholOrders = lazy(() => import("@/pages/AlcoholOrders"));
const EventCalendarPage = lazy(() => import("@/pages/EventCalendar"));
const Noticeboard = lazy(() => import("@/pages/Noticeboard"));
const PrizeVouchers = lazy(() => import("@/pages/PrizeVouchers"));
const Logins = lazy(() => import("@/pages/Logins"));
const Procedures = lazy(() => import("@/pages/Procedures"));
const ContractedRates = lazy(() => import("@/pages/ContractedRates"));
const ProcedureDetail = lazy(() => import("@/pages/ProcedureDetail"));
const Campaigns = lazy(() => import("@/pages/Campaigns"));
const Chat = lazy(() => import("@/pages/Chat"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));

function CampaignsRedirect() {
  const location = useLocation();
  return <Navigate to={`/campaigns/agent${location.search}`} replace />;
}


function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center"><Loading /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center"><Loading /></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function Shell() {
  const { user } = useAuth();
  if (user && (user.must_change_password || user.mfa_setup_required)) return <ForcedSetup />;
  return (
    <UIProvider>
      <PermissionsProvider>
        <ChatProvider>
          <AppShell />
          <GlobalModals />
          <ChatDrawer />
          <ChatWindows />
        </ChatProvider>
      </PermissionsProvider>
    </UIProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<div className="flex min-h-[50vh] items-center justify-center"><Loading /></div>}>
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route element={<Protected><Shell /></Protected>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/organisations" element={<RequirePage page="organisations"><Organisations /></RequirePage>} />
            <Route path="/organisations/:id" element={<RequirePage page="organisations"><OrganisationDetail /></RequirePage>} />
            <Route path="/contacts" element={<RequirePage page="contacts"><Contacts /></RequirePage>} />
            <Route path="/contacts/:id" element={<RequirePage page="contacts"><ContactDetail /></RequirePage>} />
            <Route path="/properties" element={<RequirePage page="properties"><Properties /></RequirePage>} />
            <Route path="/properties/:id" element={<RequirePage page="properties"><PropertyDetail /></RequirePage>} />
            <Route path="/actions/my" element={<RequirePage page="actions"><MyActions /></RequirePage>} />
            <Route path="/todo" element={<RequirePage page="todo"><ToDo /></RequirePage>} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/opportunities" element={<RequirePage page="opportunities"><Opportunities /></RequirePage>} />
            <Route path="/collaborations" element={<RequirePage page="collaborations"><Collaborations /></RequirePage>} />
            <Route path="/opportunities/:id" element={<RequirePage page="opportunities"><OpportunityDetail /></RequirePage>} />
            <Route path="/pipeline" element={<Navigate to="/opportunities" replace />} />
            <Route path="/kpis" element={<RequirePage page="kpis"><KPIs /></RequirePage>} />
            <Route path="/reports" element={<RequirePage page="reports"><Reports /></RequirePage>} />
            <Route path="/stats" element={<RequirePage page="stats"><Stats /></RequirePage>} />
            <Route path="/forecasts" element={<RequirePage page="forecasts"><Forecasts /></RequirePage>} />
            <Route path="/forecasts/:id" element={<RequirePage page="forecasts"><Forecasts /></RequirePage>} />
            <Route path="/print-orders" element={<RequirePage page="print_orders"><PrintOrders /></RequirePage>} />
            <Route path="/rates" element={<RequirePage page="actions"><RatesManagement /></RequirePage>} />
            <Route path="/alcohol-orders" element={<RequirePage page="alcohol_orders"><AlcoholOrders /></RequirePage>} />
            <Route path="/event-calendar" element={<RequirePage page="events"><EventCalendarPage /></RequirePage>} />
            <Route path="/noticeboard" element={<RequirePage page="noticeboard"><Noticeboard /></RequirePage>} />
            <Route path="/prize-vouchers" element={<RequirePage page="prize_vouchers"><PrizeVouchers /></RequirePage>} />
            <Route path="/logins" element={<RequirePage page="logins"><Logins /></RequirePage>} />
            <Route path="/procedures" element={<RequirePage page="procedures"><Procedures /></RequirePage>} />
            <Route path="/contracted-rates" element={<RequirePage page="rate_contracts"><ContractedRates /></RequirePage>} />
            <Route path="/procedures/:id" element={<RequirePage page="procedures"><ProcedureDetail /></RequirePage>} />
            <Route path="/campaigns" element={<CampaignsRedirect />} />
            <Route path="/campaigns/direct" element={<RequirePage page="campaigns"><Campaigns mode="edm" /></RequirePage>} />
            <Route path="/campaigns/edm" element={<Navigate to="/campaigns/direct" replace />} />
            <Route path="/campaigns/agent" element={<RequirePage page="campaigns"><Campaigns mode="agent" /></RequirePage>} />
            <Route path="/users" element={<Navigate to="/settings?tab=system&section=users" replace />} />
            <Route path="/roles" element={<Navigate to="/settings?tab=system&section=users" replace />} />
            <Route path="/audit-log" element={<Navigate to="/settings?tab=system&section=audit" replace />} />
            <Route path="/configuration" element={<Navigate to="/settings?tab=system&section=general" replace />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/auth/google" element={<GoogleCallback />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-center" richColors style={{ pointerEvents: "auto" }} />
    </AuthProvider>
  );
}

export default App;
